import { GameMetricsService } from './game-metrics-service';
import { AssetCatalogueService } from 'src/games/common/phaser/game/services/asset-catalogue-service';
import { assertNever, createDimensionsMap } from 'src/games/common/helper';
import { GameTranslationService } from 'src/games/common/phaser/game/services/game-translation-service';
import { GameThemeService } from 'src/games/common/phaser/game/services/game-theme-service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import {
  TwentyOneAssetType,
  TwentyOneSpriteType,
  TwentyOneStylesType,
  TwentyOneTranslationsType,
} from '../model/twentyone-types';
import { TwentyOneGameRunner } from '../../model/twentyone-game-runner';
import { TwentyOne } from 'game-logic';

export class GameServices {
  public readonly assetCatalogueService: AssetCatalogueService<
    TwentyOneAssetType,
    TwentyOneSpriteType
  >;
  public readonly gameTranslationService: GameTranslationService<TwentyOneTranslationsType>;
  public readonly gameThemeService: GameThemeService<TwentyOneStylesType>;
  public readonly gameMetricsService: GameMetricsService;
  public readonly twentyOneGameRunner: TwentyOneGameRunner;

  constructor(
    public gameListener: TwentyOne.TwentyOneListener,
    themeService: ThemeService,
    readonly useLocalDealer: boolean,
  ) {
    this.assetCatalogueService = new AssetCatalogueService(
      TwentyOneAssetType,
      GameServices.constructAssets(),
      TwentyOneSpriteType,
      GameServices.constructSprites(),
    );
    this.gameTranslationService = new GameTranslationService(
      'game.twentyone',
      Object.values(TwentyOneTranslationsType),
    );
    this.gameThemeService = new GameThemeService(
      themeService.gameFontFamily(),
      Object.values(TwentyOneStylesType),
    );
    this.gameMetricsService = new GameMetricsService();
    this.twentyOneGameRunner = new TwentyOneGameRunner(gameListener);
  }

  private static constructAssets() {
    return createDimensionsMap(TwentyOneAssetType, (value) => {
      switch (value) {
        case TwentyOneAssetType.StartButton:
          return { width: 564, height: 128 };
        case TwentyOneAssetType.PlayIcon:
          return { width: 128, height: 128 };
        case TwentyOneAssetType.CardBack:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardClub:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardDiamond:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardHeart:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardSpade:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardWild:
          return { width: 256, height: 384 };
        case TwentyOneAssetType.CardShadow:
          return { width: 315, height: 443 };
        case TwentyOneAssetType.ScoreBox:
          return { width: 230, height: 166 };
        case TwentyOneAssetType.ColumnBox:
          return { width: 230, height: 750 };
        case TwentyOneAssetType.ColumnBoxFill:
          return { width: 230, height: 750 };
        case TwentyOneAssetType.Chevron:
          return { width: 161, height: 67 };
        case TwentyOneAssetType.WildSparkle:
          return { width: 128, height: 128 };
        default:
          assertNever(value, 'Undefined game asset');
      }
    });
  }

  private static constructSprites() {
    return createDimensionsMap(TwentyOneSpriteType, (value) => {
      switch (value) {
        case TwentyOneSpriteType.NumbersLargePink:
          return { frameWidth: 170, frameHeight: 195 };
        case TwentyOneSpriteType.NumbersLargeBlack:
          return { frameWidth: 170, frameHeight: 195 };
        case TwentyOneSpriteType.NumbersSmallPink:
          return { frameWidth: 68, frameHeight: 70 };
        case TwentyOneSpriteType.NumbersSmallBlack:
          return { frameWidth: 68, frameHeight: 70 };
        default:
          assertNever(value, 'Undefined game sprite');
      }
    });
  }
}
